import axiosInstance from '../config/axios';

export interface WorkArea {
  id: number;
  name: string;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  username: string;
  is_active: boolean;
  groups: Group[];
  has_unrestricted_work_area_access: boolean;
  work_area_restrictions: WorkArea[];
}

export interface Group {
  id: number;
  name: string;
}

export interface UserGroup {
  user_id: number;
  group_id: number;
  user: User;
  group: Group;
}

export interface CreateUserGroupRequest {
  userId: number;
  groupId: number;
}

export interface UpdateUserGroupsRequest {
  group_ids: number[];
}

export interface UpdateWorkAreaAccessRequest {
  has_unrestricted_work_area_access: boolean;
  work_area_restriction_ids?: number[];
}

export interface BulkUpdateRequest {
  user_ids: number[];
  group_ids?: number[];
  has_unrestricted_work_area_access?: boolean;
  work_area_restriction_ids?: number[];
}

class PermissionService {
  async fetchAllUsers(): Promise<User[]> {
    const response = await axiosInstance.get<User[]>('/api/admin/users/');
    return response.data;
  }

  async fetchAllGroups(): Promise<Group[]> {
    const response = await axiosInstance.get<Group[]>('/api/admin/permission-groups/');
    return response.data;
  }

  async fetchAllWorkAreas(): Promise<WorkArea[]> {
    const response = await axiosInstance.get<WorkArea[]>('/api/work-areas/');
    return response.data;
  }

  async updateUserGroups(userId: number, request: UpdateUserGroupsRequest): Promise<void> {
    await axiosInstance.post(`/api/admin/users/${userId}/groups/`, request);
  }

  async updateUserWorkAreaAccess(userId: number, request: UpdateWorkAreaAccessRequest): Promise<void> {
    await axiosInstance.post(`/api/admin/users/${userId}/work_area_restrictions/`, request);
  }

  async bulkUpdate(request: BulkUpdateRequest): Promise<void> {
    await axiosInstance.post('/api/admin/users/bulk-update-groups-and-work-area/', request);
  }

  // Legacy methods - keeping for compatibility
  async addUserToGroup({userId, groupId}: CreateUserGroupRequest): Promise<void> {
    await axiosInstance.post(`/api/admin/users/${userId}/permission-groups/`, { group_id: groupId });
  }

  async removeUserFromGroup(userId: number, groupId: number): Promise<void> {
    const group = await axiosInstance.get<Group>(`/api/admin/permission-groups/${groupId}/`);
    await axiosInstance.delete(`/api/admin/users/${userId}/permission-groups/${group.data.name}`);
  }
}

export default new PermissionService();
